import { useState, useEffect, useCallback } from "react";
import { supabase } from "@/integrations/supabase/client";
import { Table, CombinedTable } from "@/types/table";
import { toast } from "sonner";

export type TableShape = 'square' | 'horizontal' | 'vertical' | 'round';

export interface TablePosition {
  tableId: string;
  type: 'single' | 'combined';
  x: number;
  y: number;
  width: number;
  height: number;
  zoneId?: string | null;
  shape?: TableShape;
  rotation?: number;
}

interface DbPosition {
  id: string;
  table_id: string;
  x: number;
  y: number;
  width: number;
  height: number;
  zone_id: string | null;
  rotation: number;
}

interface UseFloorPlanPositionsOptions {
  establishmentId?: string;
  selectedZoneId?: string | null;
  gridCols?: number;
  gridRows?: number;
}

export const useFloorPlanPositions = (
  tables: Table[], 
  combinedTables: CombinedTable[],
  options: UseFloorPlanPositionsOptions = {}
) => {
  const { establishmentId, selectedZoneId, gridCols = 12, gridRows = 8 } = options;
  const [positions, setPositions] = useState<TablePosition[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  // Generate default layout for tables without positions
  // Creates a clean, centered grid with generous spacing
  const generateDefaultPosition = useCallback((table: Table, existingPositions: TablePosition[], totalTablesCount?: number): TablePosition => {
    // All tables start as small 1x1 squares
    const tableSize = 1;
    const spacing = 2; // Generous spacing for better readability
    
    // Maximum 5 tables per row for a clean, manageable layout
    const maxTablesPerRow = Math.min(5, Math.floor(gridCols / (tableSize + spacing)));
    const index = existingPositions.length;
    
    const row = Math.floor(index / maxTablesPerRow);
    const col = index % maxTablesPerRow;
    
    // Calculate total dimensions for centering
    const totalTables = totalTablesCount || (index + 1);
    const totalRows = Math.ceil(totalTables / maxTablesPerRow);
    const tablesInThisRow = Math.min(maxTablesPerRow, totalTables - row * maxTablesPerRow);
    
    // Calculate grid dimensions
    const rowWidth = tablesInThisRow * (tableSize + spacing) - spacing;
    const gridHeight = totalRows * (tableSize + spacing) - spacing;
    
    // Center horizontally and vertically with padding
    const startX = Math.max(1, Math.floor((gridCols - rowWidth) / 2));
    const startY = Math.max(1, Math.floor((gridRows - gridHeight) / 2));

    return {
      tableId: table.id,
      type: 'single',
      x: startX + col * (tableSize + spacing),
      y: startY + row * (tableSize + spacing),
      width: tableSize,
      height: tableSize,
      zoneId: selectedZoneId || null,
    };
  }, [gridCols, gridRows, selectedZoneId]);

  // Fetch positions from Supabase
  const fetchPositions = useCallback(async () => {
    try {
      setLoading(true);
      let query = supabase
        .from('floor_plan_positions')
        .select('*');

      if (establishmentId) {
        query = query.eq('establishment_id', establishmentId);
      }

      const { data, error } = await query;

      if (error) throw error;

      const dbPositions: TablePosition[] = (data || []).map((p: DbPosition) => ({
        tableId: p.table_id,
        type: 'single' as const,
        x: p.x,
        y: p.y,
        width: p.width,
        height: p.height,
        zoneId: p.zone_id,
        rotation: p.rotation || 0,
      }));

      // Stabilisation Phase 1 : ne plus jamais auto-régénérer ni auto-insérer.
      // Les nouvelles tables sans position sont rendues côté UI dans une corbeille
      // (à placer manuellement). L'utilisateur garde le contrôle total.
      const positionedTableIds = new Set(dbPositions.map(p => p.tableId));
      const activeTables = tables.filter(t => t.isActive);
      const missingTables = activeTables.filter(t => !positionedTableIds.has(t.id));

      // On crée des positions virtuelles UNIQUEMENT en mémoire (pas en DB)
      // pour que la table apparaisse dans le plan tant que l'utilisateur ne l'a pas placée.
      // Elles ne sont persistées qu'au premier déplacement explicite.
      if (missingTables.length > 0) {
        const virtualPositions = [...dbPositions];
        for (const table of missingTables) {
          virtualPositions.push(generateDefaultPosition(table, virtualPositions));
        }
        setPositions(virtualPositions);
      } else {
        setPositions(dbPositions);
      }
    } catch (error) {
      console.error('Error fetching floor plan positions:', error);
      // Fallback to generating default positions
      const defaultPositions: TablePosition[] = [];
      tables.filter(t => t.isActive).forEach(table => {
        const pos = generateDefaultPosition(table, defaultPositions);
        defaultPositions.push(pos);
      });
      setPositions(defaultPositions);
    } finally {
      setLoading(false);
    }
  }, [tables, generateDefaultPosition, establishmentId]);

  // Save all positions to Supabase
  const savePositions = useCallback(async (newPositions: TablePosition[]) => {
    try {
      setSaving(true);
      
      // Upsert all positions
      const toUpsert = newPositions
        .filter(p => p.type === 'single')
        .map(p => ({
          table_id: p.tableId,
          x: Math.round(p.x),
          y: Math.round(p.y),
          width: Math.round(p.width),
          height: Math.round(p.height),
          establishment_id: establishmentId || null,
          zone_id: p.zoneId || null,
          rotation: p.rotation || 0,
        }));

      const { error } = await supabase
        .from('floor_plan_positions')
        .upsert(toUpsert, { onConflict: 'table_id' });

      if (error) throw error;
      
      setPositions(newPositions);
      toast.success('Plan de salle sauvegardé');
      return true;
    } catch (error) {
      console.error('Error saving floor plan positions:', error);
      toast.error('Erreur lors de la sauvegarde du plan');
      return false;
    } finally {
      setSaving(false);
    }
  }, [establishmentId]);

  // Check if a position would collide with existing tables
  const checkCollision = useCallback((
    newPosition: TablePosition,
    excludeTableId?: string
  ): boolean => {
    return positions.some(pos => {
      // Skip the table being moved
      if (pos.tableId === excludeTableId) return false;
      
      // Check if rectangles overlap
      const noOverlap = 
        newPosition.x >= pos.x + pos.width ||  // new is to the right
        newPosition.x + newPosition.width <= pos.x ||  // new is to the left
        newPosition.y >= pos.y + pos.height ||  // new is below
        newPosition.y + newPosition.height <= pos.y;   // new is above
      
      return !noOverlap;
    });
  }, [positions]);

  // Update a single position locally (for edit mode)
  const updatePositionLocal = useCallback((newPosition: TablePosition, allowCollision = false) => {
    // If collision check is enabled and there's a collision, don't update
    if (!allowCollision && checkCollision(newPosition, newPosition.tableId)) {
      return false;
    }
    
    setPositions(prev => 
      prev.map(p => 
        p.tableId === newPosition.tableId && p.type === newPosition.type 
          ? newPosition 
          : p
      )
    );
    return true;
  }, [checkCollision]);

  // Assign table to zone
  const assignToZone = useCallback((tableId: string, zoneId: string | null) => {
    setPositions(prev => 
      prev.map(p => 
        p.tableId === tableId ? { ...p, zoneId } : p
      )
    );
  }, []);

  // Reset to default layout - all tables as 1x1 squares, perfectly centered with generous spacing
  const resetToDefault = useCallback(async () => {
    const defaultPositions: TablePosition[] = [];
    const activeTables = tables.filter(t => t.isActive);
    
    if (activeTables.length === 0) {
      setPositions([]);
      return [];
    }
    
    // All tables will be 1x1 squares with generous spacing
    const tableSize = 1;
    const spacing = 2; // Generous spacing for better readability
    
    // Maximum 5 tables per row for a clean layout
    const maxTablesPerRow = Math.min(5, Math.floor(gridCols / (tableSize + spacing)));
    const tablesPerRow = Math.min(activeTables.length, maxTablesPerRow);
    
    // Calculate total dimensions
    const totalRows = Math.ceil(activeTables.length / tablesPerRow);
    const gridWidth = tablesPerRow * (tableSize + spacing) - spacing;
    const gridHeight = totalRows * (tableSize + spacing) - spacing;
    
    // Center perfectly in the visible area with minimum padding
    const startX = Math.max(1, Math.floor((gridCols - gridWidth) / 2));
    const startY = Math.max(1, Math.floor((gridRows - gridHeight) / 2));

    activeTables.forEach((table, index) => {
      const row = Math.floor(index / tablesPerRow);
      const col = index % tablesPerRow;
      
      // For incomplete last rows, center the remaining tables
      const tablesInThisRow = Math.min(tablesPerRow, activeTables.length - row * tablesPerRow);
      const rowOffset = row === totalRows - 1 && tablesInThisRow < tablesPerRow
        ? Math.floor((tablesPerRow - tablesInThisRow) * (tableSize + spacing) / 2)
        : 0;

      defaultPositions.push({
        tableId: table.id,
        type: 'single',
        x: startX + col * (tableSize + spacing) + rowOffset,
        y: startY + row * (tableSize + spacing),
        width: tableSize,
        height: tableSize,
        zoneId: selectedZoneId || null,
      });
    });

    setPositions(defaultPositions);
    return defaultPositions;
  }, [tables, gridCols, gridRows, selectedZoneId]);

  // Get positions filtered by zone
  const getPositionsByZone = useCallback((zoneId: string | null) => {
    if (zoneId === null) {
      // "All tables" - return all positions
      return positions;
    }
    if (zoneId === 'unassigned') {
      // Unassigned tables
      return positions.filter(p => !p.zoneId);
    }
    // Specific zone
    return positions.filter(p => p.zoneId === zoneId);
  }, [positions]);

  // Initial fetch
  useEffect(() => {
    if (tables.length > 0) {
      fetchPositions();
    }
  }, [tables.length]); // Only refetch when tables count changes

  return {
    positions,
    loading,
    saving,
    savePositions,
    updatePositionLocal,
    resetToDefault,
    refetch: fetchPositions,
    assignToZone,
    getPositionsByZone,
    checkCollision,
  };
};
